import { useMutation, useQuery, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

export interface SystemConfig {
  openerEngine: 'node' | 'rust';
  unwinderEngine: 'node' | 'rust';
  /**
   * Global default counter-hedge pricing mode. A per-rule `counterHedgeMode`
   * of `null` inherits this. Default `'gtc-to-sum-one'`.
   */
  counterHedgeModeDefault:
    | 'always-threshold'
    | 'gtc-to-sum-one'
    | 'market-to-hedge';
  /** Max combined sum the market-to-hedge mode will pay. Default `1.0`. */
  marketHedgeMaxSum: number;
}

/**
 * Fetches the current engine choice + global hedge config from the
 * prediction-manager.
 * GET /api/system/engine → SystemConfig
 * Polled every 30s. Returns null until the first successful response.
 */
export function useSystemConfig() {
  return useQuery<SystemConfig>({
    queryKey: ['system-config'],
    queryFn: async () => {
      const res = await predictionManagerAxiosApi.get('/api/system/engine');
      return res.data as SystemConfig;
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export interface UpdateHedgeModeDto {
  counterHedgeModeDefault?:
    | 'always-threshold'
    | 'gtc-to-sum-one'
    | 'market-to-hedge';
  marketHedgeMaxSum?: number;
}

/**
 * Updates the global counter-hedge config.
 * POST /api/system/hedge-mode → { counterHedgeModeDefault?, marketHedgeMaxSum? }
 * Invalidates the system-config query on success so the chips/controls refresh.
 */
export function useUpdateSystemConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: UpdateHedgeModeDto) => {
      const res = await predictionManagerAxiosApi.post(
        '/api/system/hedge-mode',
        dto
      );
      return res.data as SystemConfig;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['system-config']);
      },
    }
  );
}
